/**
 * Helper utilities for parsing and formatting external link logos
 *
 * The logo information is stored in the description field using JSON format:
 * {
 *   "logo": {
 *     "type": "url" | "base64",
 *     "data": "actual logo data (URL or base64 string)"
 *   },
 *   "description": "actual description text"
 * }
 *
 * For backward compatibility, if the description is plain text (not JSON),
 * it's treated as the description with no logo.
 */

export interface ExternalLinkLogo {
  type: "url" | "base64";
  data: string;
}

export interface ParsedExternalLinkData {
  logo: ExternalLinkLogo | null;
  description: string;
}

/**
 * Parse the description field to extract logo and description
 * @param descriptionField - The raw description field from the database
 * @returns Parsed logo and description data
 */
export const parseExternalLinkDescription = (
  descriptionField: string | undefined | null,
): ParsedExternalLinkData => {
  // Handle empty/null description
  if (!descriptionField || descriptionField.trim() === "") {
    return {
      logo: null,
      description: "",
    };
  }

  // Try to parse as JSON
  try {
    const parsed = JSON.parse(descriptionField);

    // Validate the structure
    if (typeof parsed === "object" && parsed !== null) {
      const logo =
        parsed.logo &&
        typeof parsed.logo === "object" &&
        (parsed.logo.type === "url" || parsed.logo.type === "base64") &&
        typeof parsed.logo.data === "string"
          ? {
              type: parsed.logo.type as "url" | "base64",
              data: parsed.logo.data,
            }
          : null;

      const description =
        typeof parsed.description === "string" ? parsed.description : "";

      return { logo, description };
    }
  } catch (e) {
    // Not JSON, treat as plain text description (backward compatibility)
  }

  // Backward compatibility: plain text description
  return {
    logo: null,
    description: descriptionField,
  };
};

/**
 * Format logo and description into the description field for storage
 * @param logo - Logo data (null if no logo)
 * @param description - Description text
 * @returns Formatted description field for database storage
 */
export const formatExternalLinkDescription = (
  logo: ExternalLinkLogo | null,
  description: string,
): string => {
  // If no logo, just return plain description (backward compatible)
  if (!logo || !logo.data || logo.data.trim() === "") {
    return description || "";
  }

  // Format as JSON
  const data = {
    logo: {
      type: logo.type,
      data: logo.data.trim(),
    },
    description: description || "",
  };

  return JSON.stringify(data);
};

/**
 * Validate if a string is a valid URL
 * @param url - URL string to validate
 * @returns true if valid URL, false otherwise
 */
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Validate if a string is a valid base64 image
 * @param base64 - Base64 string to validate
 * @returns true if valid base64 image, false otherwise
 */
export const isValidBase64Image = (base64: string): boolean => {
  // Check if it starts with data:image/
  if (!base64.startsWith("data:image/")) {
    return false;
  }

  // Check if it contains base64 marker
  if (!base64.includes("base64,")) {
    return false;
  }

  return true;
};

/**
 * Get the first letter of a title for avatar fallback
 * @param title - Link title
 * @returns First letter in uppercase
 */
export const getInitialLetter = (title: string): string => {
  if (!title || title.trim() === "") {
    return "?";
  }
  return title.trim()[0].toUpperCase();
};
